import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Tabs from "aldoo-ra/Tabs"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import {
  TrashIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  DevicePhoneMobileIcon,
  KeyIcon,
} from "@heroicons/react/24/outline"
import {
  GetLicenseDetails,
  UpdateLicense,
  DeleteLicenses,
} from "../api"
import { twMerge } from "tailwind-merge"

// Import tab components
import DetailsTab from "./tabs/DetailsTab"
import UsersTab from "./tabs/UsersTab"
import DevicesTab from "./tabs/DevicesTab"
import ActivationTab from "./tabs/ActivationTab"

export default function LicenseViewer({ licenseId, onClose }) {
  const [license, setLicense] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("details")
  const [refreshKey, setRefreshKey] = useState(0)

  // Sync activeTab with URL parameters on initial load
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search)
      const tabParam = urlParams.get('tab')
      const validTabs = ['details', 'users', 'activation']
      // Only include devices tab for non-external licenses
      // If license isn't loaded yet, we'll validate later when it loads
      if (license && !license.external) {
        validTabs.push('devices')
      } else if (!license) {
        // License not loaded yet, temporarily allow devices tab but will validate later
        validTabs.push('devices')
      }
      
      if (tabParam && validTabs.includes(tabParam)) {
        setActiveTab(tabParam)
      }
    }
  }, [license])

  // Update URL when activeTab changes
  const handleTabChange = (newTab) => {
    setActiveTab(newTab)
    if (typeof window !== "undefined") {
      const url = new URL(window.location)
      url.searchParams.set('tab', newTab)
      window.history.replaceState({}, '', url.toString())
    }
  }

  // Monitor URL changes and auto-close if URL doesn't match expected pattern
  useEffect(() => {
    const handlePopState = () => {
      if (typeof window !== "undefined") {
        const pathname = window.location.pathname
        const licenseMatch = pathname.match(/\/product-licensing\/licenses\/([^\/]+)/)

        // If URL doesn't match /product-licensing/licenses/id pattern, close viewer
        if (!licenseMatch || licenseMatch[1] !== licenseId) {
          onClose()
        } else {
          // Update activeTab based on URL parameters
          const urlParams = new URLSearchParams(window.location.search)
          const tabParam = urlParams.get('tab')
          const validTabs = ['details', 'users', 'activation']
          // Only include devices tab for non-external licenses
          if (license && !license.external) {
            validTabs.push('devices')
          } else if (!license) {
            // License not loaded yet, temporarily allow devices tab
            validTabs.push('devices')
          }
          
          if (tabParam && validTabs.includes(tabParam)) {
            setActiveTab(tabParam)
          }
        }
      }
    }

    // Listen for navigation events
    window.addEventListener('popstate', handlePopState)

    // Check once on mount
    handlePopState()

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [licenseId, onClose, license])

  // Load license details
  const loadLicenseDetails = async () => {
    try {
      setLoading(true)
      const response = await GetLicenseDetails(licenseId)

      if (response.result) {
        setLicense(response.data)
      } else {
        showNotification({
          message: "Failed to load license details",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      showNotification({
        message: "Error loading license details",
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (licenseId) {
      loadLicenseDetails()
    }
  }, [licenseId, refreshKey])

  // Handle tab switching for external licenses (when license data loads)
  useEffect(() => {
    if (license && license.external && activeTab === "devices") {
      // If current tab is devices and license is external, switch to details
      setActiveTab("details")
      // Update URL to reflect the tab change
      if (typeof window !== "undefined") {
        const url = new URL(window.location)
        url.searchParams.set('tab', 'details')
        window.history.replaceState({}, '', url.toString())
      }
    }
  }, [license, activeTab])

  // Handle license status change from dropdown
  const handleStatusChange = async (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) return

    const newStatus = selectedOptions[0].value
    if (newStatus === license.status) return // No change needed

    const statusLabels = {
      active: "activate",
      canceled: "cancel",
      paused: "pause",
      revoked: "revoke",
      free: "reset to free",
      expired: "mark as expired",
    }

    const action = statusLabels[newStatus] || newStatus

    showPromptDialog({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} License`,
      message: `Are you sure you want to ${action} this license?`,
      confirmText: "Yes",
      confirmVariant: newStatus === "revoked" ? "error_m" : "primary",
      onConfirm: async () => {
        try {
          const response = await UpdateLicense({
            licenseId: license._id,
            status: newStatus,
          })

          if (response.result) {
            showNotification({
              message: `License ${action}d successfully`,
              className: "bg-admin_primary text-admin_text_inv",
            })
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.error || `Failed to ${action} license`,
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: `An error occurred while ${action}ing license`,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle license deletion
  const handleDeleteLicense = () => {
    showPromptDialog({
      title: "Delete License",
      message: `Are you sure you want to delete license "${license.code}"? This action cannot be undone and will remove all associated data including devices and user assignments.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          const response = await DeleteLicenses([license._id])

          if (response.result) {
            showNotification({
              message: "License deleted successfully",
              className: "bg-admin_primary text-admin_text_inv",
            })
            // Navigate back to licenses list or close viewer
            onClose()
          } else {
            showNotification({
              message: response.error || "Failed to delete license",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while deleting license",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Get status config
  const getStatusConfig = (status) => {
    const configs = {
      free: {
        color: "bg-admin_progress text-white",
        label: "Free",
      },
      active: {
        color: "bg-admin_success text-white",
        label: "Active",
      },
      canceled: {
        color: "bg-admin_warning text-white",
        label: "Canceled",
      },
      paused: {
        color: "bg-admin_warning text-white",
        label: "Paused",
      },
      expired: {
        color: "bg-admin_error text-white",
        label: "Expired",
      },
      revoked: {
        color: "bg-admin_neutral_2 text-white",
        label: "Revoked",
      },
    }
    return configs[status] || configs.free
  }

  if (loading) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin_primary"></div>
      </div>
    )
  }

  if (!license) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <Typography variant="admin_body" className="text-admin_error">
          License not found
        </Typography>
      </div>
    )
  }

  const statusConfig = getStatusConfig(license.status)

  return (
    <div className="h-full flex flex-col bg-admin_canvas">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-admin_bordered bg-admin_paper">
        <div className="flex items-center space-x-4">
          <div>
            <div className="flex items-center space-x-3">
              <Typography
                component="h1"
                variant="admin_h3"
                className="text-admin_text font-mono"
              >
                {license.code}
              </Typography>

              <span
                className={twMerge(
                  "text-xs font-medium rounded-full px-3 py-1",
                  statusConfig.color
                )}
              >
                {statusConfig.label}
              </span>
            </div>

            <Typography
              variant="admin_txt1_dim"
              className="mt-1"
            >
              License ID: {license._id}
            </Typography>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Status Dropdown */}
          <div className="flex items-center space-x-2">
            <Typography variant="admin_label" className="my-0">
              Status:
            </Typography>

            <DropDownSelect
              variant="admin"
              value={[{
                value: license.status,
                label: getStatusConfig(license.status).label
              }]}
              onChange={handleStatusChange}
              options={[
                { value: "free", label: "Free" },
                { value: "active", label: "Active" },
                { value: "canceled", label: "Canceled" },
                { value: "paused", label: "Paused" },
                { value: "expired", label: "Expired" },
                { value: "revoked", label: "Revoked" },
              ]}
              className="min-w-[120px]"
              showCheckbox={false}
              enableResetButton={false}
            />
          </div>

          <Button
            variant="admin_error"
            onClick={handleDeleteLicense}
          >
            <TrashIcon className="w-5 h-5 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="flex-1 p-6">
        <Tabs
          variant="admin"
          value={activeTab}
          onChange={handleTabChange}
          tabs={(() => {
            const baseTabs = [
              { 
                value: "details", 
                label: (
                  <div className="flex items-center space-x-2">
                    <ClipboardDocumentListIcon className="w-4 h-4" />
                    <span>Details</span>
                  </div>
                )
              },
              { 
                value: "users", 
                label: (
                  <div className="flex items-center space-x-2">
                    <UsersIcon className="w-4 h-4" />
                    <span>Users</span>
                  </div>
                )
              },
            ]

            // Only show devices tab for non-external licenses
            if (license && !license.external) {
              baseTabs.push({ 
                value: "devices", 
                label: (
                  <div className="flex items-center space-x-2">
                    <DevicePhoneMobileIcon className="w-4 h-4" />
                    <span>Devices</span>
                  </div>
                )
              })
            }

            baseTabs.push({ 
              value: "activation", 
              label: (
                <div className="flex items-center space-x-2">
                  <KeyIcon className="w-4 h-4" />
                  <span>Activation Code</span>
                </div>
              )
            })

            return baseTabs
          })()}
          className="mb-6"
        />

        {/* Tab Content */}
        {activeTab === "details" && (
          <DetailsTab
            license={license}
            setLicense={setLicense}
            refreshKey={refreshKey}
            setRefreshKey={setRefreshKey}
            onClose={onClose}
          />
        )}

        {activeTab === "users" && (
          <UsersTab
            license={license}
            refreshKey={refreshKey}
            setRefreshKey={setRefreshKey}
          />
        )}

        {activeTab === "devices" && (
          <DevicesTab
            license={license}
            refreshKey={refreshKey}
            setRefreshKey={setRefreshKey}
          />
        )}

        {activeTab === "activation" && (
          <ActivationTab
            license={license}
          />
        )}
      </div>
    </div>
  )
}